import axios from 'axios';

// withCredentials: true sends the httpOnly cookie on every request automatically.
// For browsers that block third-party cookies (Safari/iOS), the token is mirrored
// into sessionStorage (not localStorage) so it is cleared on tab close and is
// not shared across tabs — smaller blast radius in case of XSS.
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000',
  withCredentials: true,
  // Without a timeout, a hung iOS-PWA connection (stale keep-alive socket
  // after the app is backgrounded/resumed) leaves requests pending forever —
  // the UI then sits on its initial state (e.g. dashboard stuck at 0) with no
  // error and no retry. A finite timeout lets those fail fast and retry.
  timeout: 25000,
});

// iOS/Android PWA session restore: sessionStorage is wiped when the app is
// closed from recents. If it's empty on load, restore from localStorage so
// that the Bearer token AND the CSRF token are both available again — without
// the CSRF restore, every POST after a PWA relaunch fails with 403.
try {
  if (!sessionStorage.getItem('access_token')) {
    const stored   = localStorage.getItem('access_token');
    const storedAt = parseInt(localStorage.getItem('access_token_at') || '0', 10);
    if (stored && (Date.now() - storedAt) < 30 * 24 * 60 * 60 * 1000) {
      sessionStorage.setItem('access_token', stored);
    }
  }
  if (!sessionStorage.getItem('csrf_token')) {
    const csrf = localStorage.getItem('csrf_token');
    if (csrf) sessionStorage.setItem('csrf_token', csrf);
  }
} catch (_) { /* storage disabled */ }

// Helper used in two places: stash a fresh CSRF + access token into both
// sessionStorage and localStorage, so subsequent requests can use Bearer
// (which bypasses CSRF middleware entirely) AND send the matching CSRF
// header for the cookie-based path.
function _stashTokens({ csrf_token, access_token }) {
  try {
    if (csrf_token) {
      sessionStorage.setItem('csrf_token', csrf_token);
      localStorage.setItem('csrf_token', csrf_token);
    }
    if (access_token) {
      sessionStorage.setItem('access_token', access_token);
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('access_token_at', String(Date.now()));
    }
  } catch (_) { /* storage disabled */ }
}

// PWA / iOS recovery: any time we have an authenticated session (cookie OR
// Bearer) but are missing the in-memory CSRF or Bearer, ask the server to
// reissue both. The refresh-csrf endpoint now also returns a fresh
// access_token so this self-heals legacy sessions that never had Bearer
// in localStorage and gets us into the Bearer-only path where CSRF is
// no longer required. Fires once on app load when needed.
(async () => {
  try {
    const hasSessionAuth   = sessionStorage.getItem('access_token');
    const hasLocalAuth     = localStorage.getItem('access_token');
    const hasSessionCsrf   = sessionStorage.getItem('csrf_token');
    // Trigger when we are missing CSRF OR missing the Bearer mirror — the
    // server-side cookie session is still valid (the GET will succeed) and
    // we'll come back with both tokens.
    if (hasSessionCsrf && hasSessionAuth) return;
    if (!hasSessionAuth && !hasLocalAuth && !document.cookie) return; // truly logged out
    const r = await API.get('/auth/refresh-csrf');
    _stashTokens(r?.data || {});
  } catch (_) { /* not authenticated or offline — ignore */ }
})();

API.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();

  // Safari/iOS fallback: send token as Authorization header if available
  const token = sessionStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // CSRF double-submit for state-changing requests
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = sessionStorage.getItem('csrf_token');
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

API.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Skip global logout for endpoints that expect a 401 as a normal outcome
    // (account switching, adding sibling accounts). Otherwise a stale saved
    // token would wipe the current session and bounce the user to /login.
    const url = (err.config?.url || '').toString();
    // refresh-csrf runs in the background during app boot — a 401 there must
    // not hard-redirect mid-render; PrivateRoute's /auth/me check handles a
    // genuinely dead session with a clean SPA navigation instead.
    const isAuthAttempt = /\/auth\/(switch-account|add-account|login|refresh-csrf)/.test(url);
    if (err.response?.status === 401 && !isAuthAttempt) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
      return Promise.reject(err);
    }

    // Network-level failure (no response received): timeout, dropped socket,
    // or the app resuming from background on iOS with a stale connection.
    // Retry idempotent GETs once after a short delay so a transient blip
    // doesn't leave the UI stuck on its initial (often all-zero) state.
    const cfg0 = err.config || {};
    const method0 = (cfg0.method || 'get').toLowerCase();
    const noResponse = !err.response; // timeout / network error
    if (noResponse && method0 === 'get' && !cfg0._netRetried) {
      cfg0._netRetried = true;
      await new Promise(r => setTimeout(r, 1200));
      return API(cfg0);
    }

    // PWA recovery: if a state-changing request was rejected because the
    // CSRF token was missing/invalid, silently fetch a fresh one and retry
    // once. Without this, PWA users whose sessionStorage was wiped on cold
    // start see "Failed to send message" until they re-login.
    const cfg = err.config || {};
    const detail = err.response?.data?.detail || '';
    const csrfRejected = err.response?.status === 403 && /csrf/i.test(detail);
    if (csrfRejected && !cfg._csrfRetried && !/\/auth\/refresh-csrf/.test(url)) {
      cfg._csrfRetried = true;
      try {
        const r = await API.get('/auth/refresh-csrf');
        _stashTokens(r?.data || {});
        const csrf = r?.data?.csrf_token;
        const access = r?.data?.access_token;
        cfg.headers = cfg.headers || {};
        if (csrf) cfg.headers['X-CSRF-Token'] = csrf;
        if (access) cfg.headers['Authorization'] = `Bearer ${access}`;
        if (csrf || access) return API(cfg);
      } catch (_) { /* fall through to reject */ }
    }
    return Promise.reject(err);
  }
);

export default API;
